import {
  AdvanceFilter,
  Facets,
  LastActiveSelection,
  ProductAttribute,
  ProductSearchFilter,
  ProductSearchFilterType,
  RefineSearchFilterData,
  UserManagementAdvanceFilter,
  UserManagementFilter,
  UserManagementFilterData,
  UserManagementFilterType,
} from 'common';
import { CheckboxItem } from '../../../../../../cf-ui/CheckboxList/components/Checkbox/CfCheckbox';
import { RadioItem } from '../../../../../../cf-ui/RadioList/Radio/CfRadio';
import { ShoppingCategoryFilterOptions } from '../RefineSearchDrawer/RefineSearchDrawer';

export const FilterDataTransform = (
  facets: Facets | undefined,
  advanceFilter: AdvanceFilter | undefined,
  canViewStateOfOrigin: boolean | undefined,
  defaultSize: string,
  shoppingCategoryFilters?: ShoppingCategoryFilterOptions
): RefineSearchFilterData => {
  const badges = facets?.Badges ? [...facets.Badges] : [];
  const packSizes = facets?.PackSizes ? [...facets.PackSizes] : [];
  const brands = facets?.Brands ? [...facets.Brands] : [];
  const storageTypes = facets?.StorageTypes ? [...facets.StorageTypes] : [];
  const categories = facets?.Categories ? [...facets.Categories] : [];
  const StateOfOriginAbbreviations = facets?.StateOfOriginAbbreviations ? [...facets.StateOfOriginAbbreviations] : [];
  const manufacturers = facets?.Manufacturers ? [...facets.Manufacturers] : [];

  if (packSizes.length > 0) {
    packSizes.unshift(defaultSize);
  }

  const productSearchFilterOptions = advanceFilter ?? defaultAdvanceFilter;

  return {
    FirstSection: {
      label: '',
      items: [
        {
          position: ProductSearchFilterType.NullableBase,
          name: 'IsLocallyStockedItem',
          label: 'Locally Stocked Items',
          value: productSearchFilterOptions.IsLocallyStockedItem,
        },
        {
          position: ProductSearchFilterType.NullableBase,
          name: 'IsCriticalItem',
          label: 'Critical Items',
          value: productSearchFilterOptions.IsCriticalItem,
        },
        {
          position: ProductSearchFilterType.NullableBase,
          name: 'IsNewInStock',
          label: 'New Item',
          value: productSearchFilterOptions.IsNewInStock,
        },
        {
          position: ProductSearchFilterType.NullableBase,
          name: 'HasPreviousPurchase',
          label: 'Previously Purchased',
          value: productSearchFilterOptions.HasPreviousPurchase,
        },
        {
          position: ProductSearchFilterType.NullableBase,
          name: 'IsEarlyCutoffItem',
          label: 'Early Cutoff',
          value: productSearchFilterOptions.IsEarlyCutoffItem,
        },
        {
          position: ProductSearchFilterType.NullableBase,
          name: 'IsExclusiveProduct',
          label: 'Our Brands',
          value: productSearchFilterOptions.IsExclusiveProduct,
        },
      ],
    },
    ShoppingCategoryFilters: shoppingCategoryFilters
      ? {
          label: shoppingCategoryFilters.title,
          items: shoppingCategoryFilters.items.map((x) =>
            generateFilterFromString(
              'ShoppingCategoryFilters',
              x,
              ProductSearchFilterType.ShoppingCategoryFilters,
              productSearchFilterOptions.ShoppingCategoryFilters &&
                productSearchFilterOptions.ShoppingCategoryFilters.indexOf(x) >= 0
            )
          ),
        }
      : undefined,
    Categories: {
      label: 'Category',
      items: categories.map((x) =>
        generateFilterFromString(
          'CategoryIds',
          x,
          ProductSearchFilterType.CategoryIds,
          productSearchFilterOptions.CategoryIds.indexOf(x) >= 0
        )
      ),
    },
    StorageTypes: {
      label: 'Storage Type',
      items: storageTypes.map((x) =>
        generateFilterFromString(
          'StorageTypes',
          x,
          ProductSearchFilterType.Badges,
          productSearchFilterOptions.StorageTypes.indexOf(x) >= 0
        )
      ),
    },
    Brands: {
      label: 'Brand',
      items: brands.map((x) =>
        generateFilterFromString(
          'Brands',
          x,
          ProductSearchFilterType.Brands,
          productSearchFilterOptions.Brands.indexOf(x) >= 0
        )
      ),
    },
    ...(canViewStateOfOrigin && {
      StateOfOriginAbbreviations: {
        label: 'State of Origin',
        items: StateOfOriginAbbreviations.map((x) =>
          generateFilterFromString(
            'StateOfOriginAbbreviations',
            x,
            ProductSearchFilterType.StateOfOriginAbbreviations,
            productSearchFilterOptions.StateOfOriginAbbreviations.indexOf(x) >= 0
          )
        ),
      },
    }),
    ProductAttributes: {
      label: 'Product Attributes',
      items: [
        {
          label: 'Badges',
          items: badges.map((x) =>
            generateFilterFromString(
              'Badges',
              x,
              ProductSearchFilterType.Badges,
              productSearchFilterOptions.Badges.indexOf(x) >= 0
            )
          ),
        },
        {
          label: 'Nutritional',
          items: [
            {
              position: ProductSearchFilterType.Nutritional,
              name: 'IsChildNutrition',
              label: 'Child Nutrition',
              value: productSearchFilterOptions.Nutritional.IsChildNutrition,
            },
            {
              position: ProductSearchFilterType.Nutritional,
              name: 'IsGlutenFree',
              label: 'Gluten Free',
              value: productSearchFilterOptions.Nutritional.IsGlutenFree,
            },
            {
              position: ProductSearchFilterType.Nutritional,
              name: 'IsKosher',
              label: 'Kosher',
              value: productSearchFilterOptions.Nutritional.IsKosher,
            },
            {
              position: ProductSearchFilterType.Nutritional,
              name: 'FreeFromTreeNuts',
              label: 'Nut Free',
              value: productSearchFilterOptions.Nutritional.FreeFromTreeNuts,
            },
            {
              position: ProductSearchFilterType.Nutritional,
              name: 'IsVegan',
              label: 'Vegan',
              value: productSearchFilterOptions.Nutritional.IsVegan,
            },

            {
              position: ProductSearchFilterType.Nutritional,
              name: 'IsCatchWeight',
              label: 'Catch Weight',
              value: productSearchFilterOptions.Nutritional.IsCatchWeight,
            },
            {
              position: ProductSearchFilterType.Nutritional,
              name: 'IsHalal',
              label: 'Halal',
              value: productSearchFilterOptions.Nutritional.IsHalal,
            },
            {
              position: ProductSearchFilterType.Nutritional,
              name: 'IsOrganic',
              label: 'Organic',
              value: productSearchFilterOptions.Nutritional.IsOrganic,
            },
            {
              position: ProductSearchFilterType.Nutritional,
              name: 'FreeFromCrustacean',
              label: 'Crustacean Free',
              value: productSearchFilterOptions.Nutritional.FreeFromCrustacean,
            },
            {
              position: ProductSearchFilterType.Nutritional,
              name: 'FreeFromEggs',
              label: 'Egg Free',
              value: productSearchFilterOptions.Nutritional.FreeFromEggs,
            },
            {
              position: ProductSearchFilterType.Nutritional,
              name: 'FreeFromMilk',
              label: 'Milk Free',
              value: productSearchFilterOptions.Nutritional.FreeFromMilk,
            },
            {
              position: ProductSearchFilterType.Nutritional,
              name: 'FreeFromWheat',
              label: 'Wheat Free',
              value: productSearchFilterOptions.Nutritional.FreeFromWheat,
            },
            {
              position: ProductSearchFilterType.Nutritional,
              name: 'FreeFromSoy',
              label: 'Soy Free',
              value: productSearchFilterOptions.Nutritional.FreeFromSoy,
            },
            {
              position: ProductSearchFilterType.Nutritional,
              name: 'FreeFromPeanuts',
              label: 'Peanut Free',
              value: productSearchFilterOptions.Nutritional.FreeFromPeanuts,
            },
            {
              position: ProductSearchFilterType.Nutritional,
              name: 'FreeFromTransFat',
              label: 'Trans Fat Free',
              value: productSearchFilterOptions.Nutritional.FreeFromTransFat,
            },
          ],
        },
        {
          label: 'Origin',
          items: [
            {
              position: ProductSearchFilterType.Origin,
              name: 'IsDomestic',
              label: 'Domestic',
              value: productSearchFilterOptions.Nutritional.IsDomestic,
            },
          ],
        },
        {
          label: 'Program',
          items: [
            {
              position: ProductSearchFilterType.Program,
              name: 'IsSnackItem',
              label: 'Snack Item',
              value: productSearchFilterOptions.Nutritional.IsSnackItem,
            },
            {
              position: ProductSearchFilterType.Program,
              name: 'HasMSDS',
              label: 'MSDS',
              value: productSearchFilterOptions.Nutritional.HasMSDS,
            },
          ],
        },
      ],
    },
    Manufacturers: {
      label: 'Manufacturer',
      items: manufacturers.map((x) =>
        generateFilterFromString(
          'Manufacturers',
          x,
          ProductSearchFilterType.Manufacturers,
          productSearchFilterOptions.Manufacturers.indexOf(x) >= 0
        )
      ),
    },
  };
};

export const defaultAdvanceFilter: AdvanceFilter = {
  IsLocallyStockedItem: undefined,
  IsCriticalItem: undefined,
  IsNewInStock: undefined,
  HasOrderedInLastNumberOfDays: undefined,
  HasPreviousPurchase: undefined,
  Badges: [],
  CategoryIds: [],
  Brand: undefined,
  Brands: [],
  PackSize: undefined,
  StorageTypes: [],
  StateOfOriginAbbreviation: undefined,
  StateOfOriginAbbreviations: [],
  DeliveryOptions: {},
  Nutritional: {},
  Manufacturers: [],
};

const generateFilterFromString = (
  name: string,
  item: string,
  position: ProductSearchFilterType,
  value: unknown
): ProductSearchFilter => {
  return {
    position: position,
    name: name,
    label: item,
    value: value,
  };
};

export const convertProductSearchFilterToCheckBoxItem = (filter: ProductSearchFilter): CheckboxItem => {
  return {
    additionalValue: filter,
    name: filter.name,
    label: filter.label || '',
    checked: filter.value,
  };
};

export const convertProductSearchFilterToCheckBoxGroup = (filter: ProductAttribute): CheckboxGroup => {
  return {
    label: filter.label || '',
    items: filter.items.map((x) => convertProductSearchFilterToCheckBoxItem(x)),
  };
};

export type CheckboxGroup = {
  label: string;
  items: CheckboxItem[];
  onCheckedChange?: (label: string, item: CheckboxItem) => void;
};

export const convertUserManagementFilterToCheckBoxItem = (filter: UserManagementFilter): CheckboxItem => {
  return {
    additionalValue: filter,
    name: filter.name,
    label: filter.label || '',
    checked: filter.value,
    disabled: filter.disabled,
  };
};

export const convertUserManagementFilterToRadioItem = (filter: UserManagementFilter): RadioItem => {
  return {
    label: filter.label || '',
    value: filter.value,
    sx: { mb: 1, '& .MuiTypography-root': { typography: 'responsiveParagraph' } },
  };
};

export const UserManagementFilterDataTransform = (
  advanceFilter: UserManagementAdvanceFilter | undefined
): UserManagementFilterData => {
  const UserManagementFilterOptions = advanceFilter ?? defaultUserManagementAdvanceFilterAdvanceFilter;

  return {
    Status: {
      label: 'Status',
      items: [
        {
          position: UserManagementFilterType.Status,
          name: 'IncludeActiveUsers',
          label: 'Active',
          value: UserManagementFilterOptions.IncludeActiveUsers,
          disabled: false,
        },
        {
          position: UserManagementFilterType.Status,
          name: 'IncludePendingUsers',
          label: 'Pending',
          value: UserManagementFilterOptions.IncludePendingUsers,
          disabled: false,
        },
        {
          position: UserManagementFilterType.Status,
          name: 'IncludeExpiredUsers',
          label: 'Expired',
          value: UserManagementFilterOptions.IncludeExpiredUsers,
          disabled: false,
        },

        {
          position: UserManagementFilterType.Status,
          name: 'IncludeInactiveUsers',
          label: 'Inactive',
          value: UserManagementFilterOptions.IncludeInactiveUsers,
          disabled: false,
        },
      ],
    },
    LastActive: {
      label: 'Last Active',
      items: [
        {
          position: UserManagementFilterType.LastActive,
          name: 'All',
          label: 'All',
          value: LastActiveSelection.All,
          additionalValue: 0,
        },
        {
          position: UserManagementFilterType.LastActive,
          name: 'MoreThan30Days',
          label: 'More than 30 days ago',
          value: LastActiveSelection.MoreThan30Days,
          additionalValue: 1,
        },
        {
          position: UserManagementFilterType.LastActive,
          name: 'MoreThan60Days',
          label: 'More than 60 days ago',
          value: LastActiveSelection.MoreThan60Days,
          additionalValue: 2,
        },
        {
          position: UserManagementFilterType.LastActive,
          name: 'MoreThan90Days',
          label: 'More than 90 days ago',
          value: LastActiveSelection.MoreThan90Days,
          additionalValue: 3,
        },

        {
          position: UserManagementFilterType.LastActive,
          name: 'MoreThan6Months',
          label: 'More than 6 months ago',
          value: LastActiveSelection.MoreThan6Months,
          additionalValue: 6,
        },

        {
          position: UserManagementFilterType.LastActive,
          name: 'MoreThan1Year',
          label: 'More than 1 year ago',
          value: LastActiveSelection.MoreThan1Year,
          additionalValue: 12,
        },
      ],
    },
  };
};

export const defaultUserManagementAdvanceFilterAdvanceFilter: UserManagementAdvanceFilter = {
  IncludeActiveUsers: true,
  IncludePendingUsers: true,
  IncludeExpiredUsers: true,
  IncludeInactiveUsers: true,
};
